/**
 * R5 — MailEditorModal.jsx
 *
 * Full-featured email composer replacing the inline modal inside
 * SendMailButton. Exposes Da/A/Cc/Oggetto, rich-ish textarea with a
 * mini-toolbar, attachment toggle, HTML preview + Draft save.
 *
 * Props:
 *   - open, onClose
 *   - offerId                  (required for draft save/load)
 *   - from                     (read-only current user email)
 *   - defaultTo / defaultSubject / defaultBody / defaultCc
 *   - onSent()                 callback after successful send
 */
import { useEffect, useState } from "react";
import {
  Mail,
  Loader2,
  Save,
  Paperclip,
  Eye,
  EyeOff,
  X,
  Bold,
  Italic,
  List as ListIcon,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { api, formatErrorDetail } from "../lib/api";

import { API } from "../lib/backend";

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Very small "markdown-lite" renderer (bold **x**, italic _x_, lists "- "). */
function renderPreview(body, attachFormNote) {
  let html = escapeHtml(body);
  html = html
    .replace(/\*\*([^\*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|\s)_([^_]+)_(?=\s|$)/g, "$1<em>$2</em>")
    .replace(/\n/g, "<br/>");
  // Tiny list heuristic
  html = html.replace(
    /(?:<br\/>|^)(?:- )([^<]+)/g,
    "<br/>• $1"
  );
  if (attachFormNote) {
    html +=
      "<br/><br/><em style='color:#475569'>" +
      "Allegato: modulo anagrafica nuovo cliente</em>";
  }
  return html;
}

export default function MailEditorModal({
  open,
  onClose,
  offerId,
  from,
  defaultTo = "",
  defaultCc = "",
  defaultSubject = "",
  defaultBody = "",
  onSent,
}) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [attachForm, setAttachForm] = useState(false);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Load any existing draft when the dialog opens
  useEffect(() => {
    if (!open || !offerId || draftLoaded) return;
    api
      .draftEmailGet(offerId)
      .then((d) => {
        const dr = d?.draft;
        if (dr) {
          setTo(dr.to || defaultTo);
          setCc(dr.cc || defaultCc);
          setSubject(dr.subject || defaultSubject);
          setBody(dr.body || defaultBody);
          setAttachForm(!!dr.attach_customer_form);
        }
      })
      .catch(() => {})
      .finally(() => setDraftLoaded(true));
  }, [open, offerId, draftLoaded, defaultTo, defaultCc, defaultSubject, defaultBody]);

  // Reset draftLoaded when closing so next open re-fetches
  useEffect(() => {
    if (!open) setDraftLoaded(false);
  }, [open]);

  const insertAround = (prefix, suffix = prefix) => {
    const ta = document.querySelector('[data-testid="mail-body-textarea"]');
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = body.slice(0, start);
    const sel = body.slice(start, end);
    const after = body.slice(end);
    const wrapped = `${before}${prefix}${sel || "testo"}${suffix}${after}`;
    setBody(wrapped);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length + (sel || "testo").length);
    }, 10);
  };

  const saveDraft = async () => {
    if (!offerId) return;
    setSavingDraft(true);
    try {
      await api.draftEmailSave(offerId, {
        to,
        cc,
        subject,
        body,
        attach_customer_form: attachForm,
      });
      toast.success("Bozza salvata");
    } catch (e) {
      toast.error(formatErrorDetail(e));
    } finally {
      setSavingDraft(false);
    }
  };

  const send = async () => {
    if (!to || !body) return;
    setBusy(true);
    try {
      const bodyHtml = `<div style="font-family:Arial,sans-serif">${renderPreview(
        body,
        attachForm
      )}</div>`;
      await axios.post(
        `${API}/offers/${offerId}/send-mail`,
        {
          to,
          cc: cc || undefined,
          subject,
          body_html: bodyHtml,
        },
        { withCredentials: true }
      );
      toast.success(`Mail inviata a ${to}`);
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(formatErrorDetail(e) || "Invio fallito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent
        className="max-w-[760px] max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="mail-editor-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" style={{ color: "var(--accent-primary)" }} />
            Invia messaggio al cliente
          </DialogTitle>
          <DialogDescription>
            Rivedi e modifica prima di inviare. Puoi salvare una bozza e
            inviarla più tardi.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-3 pr-1">
          {/* Header fields */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span
                className="w-14 text-[11px] uppercase tracking-wide shrink-0"
                style={{ color: "var(--text-muted)" }}
              >
                Da
              </span>
              <div
                className="flex-1 h-9 px-3 rounded-md text-[13px] flex items-center"
                style={{
                  background: "var(--bg-subtle)",
                  border: "1px solid var(--border-subtle)",
                  color: "var(--text-secondary)",
                }}
                data-testid="mail-from"
              >
                {from || "—"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-14 text-[11px] uppercase tracking-wide shrink-0"
                style={{ color: "var(--text-muted)" }}
              >
                A
              </span>
              <input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="cliente@esempio.it"
                className="flex-1 h-9 px-3 rounded-md text-[13px]"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid="mail-to"
              />
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-14 text-[11px] uppercase tracking-wide shrink-0"
                style={{ color: "var(--text-muted)" }}
              >
                Cc
              </span>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="opzionale, più indirizzi separati da virgola"
                className="flex-1 h-9 px-3 rounded-md text-[13px]"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid="mail-cc"
              />
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-14 text-[11px] uppercase tracking-wide shrink-0"
                style={{ color: "var(--text-muted)" }}
              >
                Oggetto
              </span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Oggetto della mail"
                className="flex-1 h-9 px-3 rounded-md text-[13px]"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid="mail-subject"
              />
            </div>
          </div>

          {/* Toolbar */}
          <div
            className="flex items-center gap-1 px-1 py-1 rounded-md"
            style={{ background: "var(--bg-subtle)" }}
          >
            <ToolButton onClick={() => insertAround("**")} testId="tool-bold">
              <Bold className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton onClick={() => insertAround("_")} testId="tool-italic">
              <Italic className="h-3.5 w-3.5" />
            </ToolButton>
            <ToolButton
              onClick={() =>
                setBody((b) => (b ? `${b}\n- ` : "- "))
              }
              testId="tool-list"
            >
              <ListIcon className="h-3.5 w-3.5" />
            </ToolButton>
            <span className="flex-1" />
            <ToolButton
              onClick={() => setPreview((p) => !p)}
              active={preview}
              testId="tool-preview-toggle"
            >
              {preview ? (
                <EyeOff className="h-3.5 w-3.5" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
              <span className="text-[11px] ml-1">
                {preview ? "Modifica" : "Anteprima"}
              </span>
            </ToolButton>
          </div>

          {/* Body or preview */}
          {!preview ? (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Scrivi qui il messaggio… usa **grassetto**, _corsivo_, '- ' per elenchi."
              className="w-full text-[13px] p-3 rounded-md resize-vertical"
              style={{
                border: "1px solid var(--border-subtle)",
                fontFamily: "Inter, sans-serif",
                lineHeight: 1.55,
                minHeight: 200,
              }}
              data-testid="mail-body-textarea"
            />
          ) : (
            <div
              className="text-[13px] p-3 rounded-md overflow-auto"
              style={{
                border: "1px solid var(--border-subtle)",
                background: "white",
                minHeight: 200,
                maxHeight: 320,
              }}
              dangerouslySetInnerHTML={{
                __html: renderPreview(body, attachForm),
              }}
              data-testid="mail-preview"
            />
          )}

          {/* Attachments toggle */}
          <label
            className="flex items-center gap-2 cursor-pointer text-[12px]"
            style={{ color: "var(--text-secondary)" }}
          >
            <input
              type="checkbox"
              checked={attachForm}
              onChange={(e) => setAttachForm(e.target.checked)}
              className="accent-[var(--accent-primary)]"
              data-testid="mail-attach-form"
            />
            <Paperclip className="h-3 w-3" />
            Allega modulo anagrafica nuovo cliente
          </label>
        </div>

        {/* Actions */}
        <div
          className="flex items-center justify-between gap-2 pt-3 border-t"
          style={{ borderColor: "var(--border-subtle)" }}
        >
          <button
            type="button"
            onClick={saveDraft}
            disabled={savingDraft || !offerId}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-medium disabled:opacity-40"
            style={{
              color: "var(--text-primary)",
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
            }}
            data-testid="mail-save-draft-btn"
          >
            {savingDraft ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            Salva bozza
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="h-8 px-3 rounded-md text-[12px]"
              style={{
                border: "1px solid var(--border-subtle)",
                color: "var(--text-primary)",
              }}
              data-testid="mail-cancel-btn"
            >
              <X className="h-3 w-3 inline mr-1" />
              Annulla
            </button>
            <button
              type="button"
              onClick={send}
              disabled={busy || !to || !body || !subject}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-medium text-white disabled:opacity-40"
              style={{ background: "var(--accent-primary)" }}
              data-testid="mail-send-btn"
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Mail className="h-3 w-3" />
              )}
              Invia
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToolButton({ children, onClick, testId, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-0.5 h-6 px-1.5 rounded text-[12px] transition-colors"
      style={{
        background: active ? "var(--accent-subtle)" : "transparent",
        color: active ? "var(--accent-primary)" : "var(--text-secondary)",
      }}
      onMouseEnter={(e) => {
        if (!active)
          e.currentTarget.style.background = "var(--bg-surface)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
